/**
 * The inspector's cover plate: the photograph, what the plan is, and its figures.
 *
 * ── Over a TEMPLATE nothing here waits; over a CLIENT'S PLAN the day figure does ─────────────
 * A template's plate is built entirely from the list row the trainer pointed at, which arrived with
 * the screen — the cover, the category, the name, the rhythm, the mode and the follower count are
 * all on it. So a swap paints a complete plate on its first frame, and `/full` fills in only what is
 * below the plate.
 *
 * A client's plan has no such row. Its list row is an ASSIGNMENT, and `PlanAssignmentSummary`
 * carries no phases, so the day figure arrives with `/full` as `InspectorView.overview` and stands
 * as the em dash until then. The dash is one line like the figure that replaces it, so nothing moves.
 *
 * ── The figures, after the owner's polish of 4 November 2026 ─────────────────────────────────
 * Two figures and a mark: the days in the repeat unit, then the followers (a template) or the
 * progress (a client's plan), then the mode's symbol alone at the row's far right. The sub-line
 * (`4 tygodnie · 3 dni / tydz. · utworzony…`) went with its reserved line, and the exercise count
 * went with it: the span is on the list row beside the pane, and the rhythm is the first figure.
 *
 * ── A dash under a figure means the plan cannot state it, and the sentence beside it says which ─
 * The specimen's own rule for the follower count: `P.cl.length ? String(P.cl.length) : '—'`. That is
 * a different dash from the page header's, which stands for a figure nobody knows YET, and one glyph
 * carrying two meanings on one screen is exactly what "never encode meaning in a mark alone" is
 * about. So the zero case carries the library's own sentence for it, hidden, and a reader hears
 * "nobody is following this programme yet" rather than a dash.
 *
 * ── `Duplikuj` is not here, and that is owner decision D2 ────────────────────────────────────
 * The canon draws a ghost button beside `Edytuj`. There is no duplicate-plan endpoint and no plan
 * to add one this round; a control that cannot act is worse than an absent one. Dropping it moves
 * nothing — `Edytuj` is pinned to the plate's right edge by a flexible spacer, exactly as the
 * specimen pins it.
 *
 * ── The plate's ink is white in BOTH themes, and its tokens say so ───────────────────────────
 * A photograph under a dark veil is a photograph in both themes. `var(--text)` here would be black
 * on Flame and invisible; `--text-on-photo` and the two `--…-on-cover` steps are theme-independent
 * for the same reason `--photo-scrim` is.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import photoBox from '~/shared/ui/photoBox.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { InspectorSubject } from '../model/inspectorSubject';
import { daysInRepeatUnit, type InspectorView } from '../model/inspectorView';
import { PlanModeMark } from './PlanModeMark';
import styles from './InspectorHero.module.css';

interface InspectorHeroProps {
  readonly subject: InspectorSubject;
  /** The plan itself. `null` while `/full` is open; a client's plan's day figure waits on it. */
  readonly plan: InspectorView | null;
}

/** One figure and the word under it. `figure === null` is the canon's em dash. */
interface Kpi {
  readonly key: string;
  /** Pre-formatted, because the second one is a percent over a client's plan and a count otherwise. */
  readonly figure: string | null;
  readonly label: string;
  /** Said instead of the mark when the figure alone would not carry the fact. */
  readonly spoken?: string;
}

export function InspectorHero({ subject, plan }: InspectorHeroProps) {
  const { t } = useTranslation();

  const selfPaced = subject.row.schedulingMarker === 'selfPaced';
  // WHICH source answers the day figure is `model/`'s decision, not this file's: a template quotes
  // its list row, and a client's plan has no list row to quote, so it passes no rhythm and the
  // plan's own tree answers.
  const days = daysInRepeatUnit(
    subject.kind === 'template' ? subject.row.rhythm : null,
    plan?.overview ?? null,
  );
  // The DAY figure's label is the mode's: a weekly plan counts days in a week, a self-paced one
  // counts days in its cycle, and the two are different sentences rather than one with a variable
  // noun.
  const daysKpi: Kpi = {
    key: 'days',
    figure: days === null ? null : String(days),
    label: t(
      selfPaced
        ? 'web:planLibrary.inspector.kpi.daysInCycle'
        : 'web:planLibrary.inspector.kpi.daysPerWeek',
      { count: days ?? 0 },
    ),
  };

  const paused = subject.kind === 'clientPlan' && subject.row.status === 'paused';
  const progressCount =
    subject.kind === 'template'
      ? ''
      : t('web:planLibrary.inspector.kpiProgress', {
          count: subject.row.workoutsTotal,
          done: subject.row.workoutsDone,
        });

  const kpis: readonly Kpi[] =
    subject.kind === 'template'
      ? [
          daysKpi,
          {
            key: 'assigned',
            // The specimen's own rule: a zero prints as the dash rather than as `0`.
            figure:
              subject.row.assignedClientCount === 0
                ? null
                : String(subject.row.assignedClientCount),
            label: t('web:planLibrary.stats.assigned'),
            spoken:
              subject.row.assignedClientCount === 0
                ? t('web:planLibrary.noClients')
                : t('web:planLibrary.row.clients', { count: subject.row.assignedClientCount }),
          },
        ]
      : [
          daysKpi,
          {
            key: 'progress',
            // The figure is the PERCENT and the word under it is the count it came from, which is
            // owner decision D7 read onto the plate: `40%` over `12 z 30 treningów`.
            figure: t('web:planLibrary.clientPlans.percent', {
              percent: subject.row.progressPct,
            }),
            // A PAUSED plan says so here, and the pane said it nowhere else: an inspector over a
            // paused assignment was byte-identical to one over a live assignment on the same
            // template, and triage is the trainer's whole task on this screen. The row's own word,
            // in front of the count rather than instead of it — the figures are still true.
            label: paused
              ? `${t('web:planLibrary.clientPlans.paused')} · ${progressCount}`
              : progressCount,
          },
        ];

  const title = subject.kind === 'template' ? subject.row.name : subject.row.planName;

  return (
    <div className={styles.hero}>
      <img
        className={`${String(photoBox.photo)} ${String(styles.cover)}`}
        src={subject.row.heroUrl}
        alt=""
        decoding="async"
      />
      <span className={styles.veil} aria-hidden />

      <span className={styles.top}>
        {subject.row.category === null ? null : (
          <span className={styles.chip}>
            <i className={styles.chipDot} aria-hidden />
            {subject.row.category}
          </span>
        )}
        <span className={styles.spacer} aria-hidden />
        <Link
          className={styles.edit}
          to="/plans/$planId"
          // A client's plan opens THEIR fork, not the template it came from.
          params={{ planId: String(subject.planId) }}
        >
          {t('web:planLibrary.inspector.edit')}
        </Link>
      </span>

      <span className={styles.foot}>
        <h2 className={styles.title}>{title}</h2>
        <span className={styles.kpis}>
          {kpis.map((kpi) => (
            <span key={kpi.key} className={styles.kpi}>
              <span className={styles.figure} aria-hidden={kpi.spoken !== undefined}>
                {kpi.figure ?? t('web:planLibrary.states.figureUnknown')}
              </span>
              <span className={styles.label} aria-hidden={kpi.spoken !== undefined}>
                {kpi.label}
              </span>
              {kpi.spoken === undefined ? null : (
                <span className={hidden.srOnly}>{kpi.spoken}</span>
              )}
            </span>
          ))}
          {/* The mode's symbol alone, at the row's far right; its name is the mark's hidden text. */}
          <PlanModeMark marker={subject.row.schedulingMarker} className={styles.mode} />
        </span>
      </span>
    </div>
  );
}
